// Left menu definition (doc 00 §5). Items are filtered by permission in the shell; the server
// enforces the same permissions on every route.
import type { Permission } from '@twinlink/shared';
import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiBarChart2,
  FiBell,
  FiBox,
  FiLayers,
  FiBriefcase,
  FiClipboard,
  FiCode,
  FiCompass,
  FiCpu,
  FiDatabase,
  FiGitBranch,
  FiGrid,
  FiHeart,
  FiHome,
  FiMapPin,
  FiSettings,
  FiShield,
  FiSliders,
  FiUsers,
} from 'react-icons/fi';

export type NavSection = 'overview' | 'operations' | 'administration' | 'platform';

export interface NavItem {
  key: string;
  /** Path relative to the org base (`/o/:orgId`) or absolute for platform pages. */
  path: string;
  scope: 'org' | 'platform';
  section: NavSection;
  icon: IconType;
  /** Shown when the user holds at least one of these (empty = every member). */
  anyOf: Permission[];
  /** Hidden for the Client role, which only gets shared dashboards and schematics. */
  hideForClient?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { key: 'home', path: '', scope: 'org', section: 'overview', icon: FiHome, anyOf: [] },
  { key: 'assets', path: 'assets', scope: 'org', section: 'overview', icon: FiMapPin, anyOf: ['assets.view'], hideForClient: true },
  { key: 'live', path: 'live', scope: 'org', section: 'operations', icon: FiActivity, anyOf: ['dashboards.view'], hideForClient: true },
  { key: 'dashboards', path: 'dashboards', scope: 'org', section: 'operations', icon: FiGrid, anyOf: ['dashboards.view'] },
  { key: 'schematics', path: 'schematics', scope: 'org', section: 'operations', icon: FiGitBranch, anyOf: ['schematics.view'] },
  { key: 'alerts', path: 'alerts', scope: 'org', section: 'operations', icon: FiBell, anyOf: ['alerts.view'] },
  { key: 'explorer', path: 'explorer', scope: 'org', section: 'operations', icon: FiCompass, anyOf: ['explorer.use', 'signals.manage'] },
  { key: 'adminUsers', path: 'admin/users', scope: 'org', section: 'administration', icon: FiUsers, anyOf: ['org.users.manage'] },
  { key: 'adminDataSources', path: 'admin/datasources', scope: 'org', section: 'administration', icon: FiDatabase, anyOf: ['datasources.manage'] },
  { key: 'adminUnits', path: 'admin/units', scope: 'org', section: 'administration', icon: FiSliders, anyOf: ['signals.manage'] },
  { key: 'adminSettings', path: 'admin/settings', scope: 'org', section: 'administration', icon: FiSettings, anyOf: ['org.settings.manage'] },
  { key: 'adminAudit', path: 'admin/audit', scope: 'org', section: 'administration', icon: FiClipboard, anyOf: ['org.audit.view'] },
  { key: 'platformOrgs', path: '/platform/orgs', scope: 'platform', section: 'platform', icon: FiBriefcase, anyOf: ['platform.orgs.manage', 'platform.health.view'] },
  { key: 'platformUsers', path: '/platform/users', scope: 'platform', section: 'platform', icon: FiShield, anyOf: ['platform.users.manage', 'platform.users.unlock'] },
  { key: 'platformEquipment', path: '/platform/equipment', scope: 'platform', section: 'platform', icon: FiBox, anyOf: ['platform.templates.manage', 'platform.health.view'] },
  { key: 'platformTemplates', path: '/platform/templates', scope: 'platform', section: 'platform', icon: FiLayers, anyOf: ['platform.templates.manage', 'platform.health.view'] },
  { key: 'platformAssistant', path: '/platform/assistant', scope: 'platform', section: 'platform', icon: FiCpu, anyOf: ['platform.orgs.manage', 'platform.health.view'] },
  { key: 'platformAudit', path: '/platform/audit', scope: 'platform', section: 'platform', icon: FiBarChart2, anyOf: ['platform.health.view'] },
  { key: 'platformHealth', path: '/platform/health', scope: 'platform', section: 'platform', icon: FiHeart, anyOf: ['platform.health.view'] },
  { key: 'platformApi', path: '/platform/api', scope: 'platform', section: 'platform', icon: FiCode, anyOf: ['platform.health.view'] },
];

export const NAV_SECTIONS: NavSection[] = ['overview', 'operations', 'administration', 'platform'];
